import axios from 'axios';

const { REACT_APP_API_URL } = process.env;

const axiosInstance = axios.create({
    withCredentials: true,
    baseURL: REACT_APP_API_URL + "/api/"
});

const config = {
    headers: {
        "Content-Type": "application/json"
    }
}

const ApiService = {
    get: (path) => {
        return axiosInstance.get(path, config)
            .then(response => {
                return response.data;
            })
            .catch(err => {
                throw err;
            });
    },
    post: (path, payload) => {
        return axiosInstance.post(path, payload, config)
            .then(response => {
                return response.data;
            })
            .catch(err => {
                throw err;
            });
    },
    put: (path, payload) => {
        return axiosInstance.put(path, payload, config)
    },
}

export const UserAPIService = {
    login: (credentials) => {
        console.log('Logging in')
        return ApiService.post("users/login", credentials);
    },
    verifyInvite: (credentials) => {
        return ApiService.post("users/verify-invite", credentials);
    },
    register: (credentials) => {
        return ApiService.post("users/register", credentials);
    },
    anonymousRegister: (credentials) => {
        return ApiService.post("users/anonymous-register", credentials);
    },
    registerByEmail: (payload) => {
        return ApiService.post("users/register-by-email", payload)
    },
    getHearAboutUsOptions: () => {
        return ApiService.get("user-hear-about-options");
    },
    getIndustryOptions: () => {
        return ApiService.get("users/industry-options");
    },
    checkOrganizationAvailability: (companyName) => {
        return ApiService.get(`users/check-organization/${encodeURIComponent(companyName)}`);
    },
    checkEmailAvailability: (email) => {
        return ApiService.get(`users/check-email/${encodeURIComponent(email)}`);
    },
    registerExistingUserCompany: (credentials) => {
        return ApiService.post("users/register-existing-user-company", credentials);
    },
    contact: (form) => {
        return ApiService.post("users/contact", form)
    },
    userDetailsByEmail: (email) => {
        return ApiService.get(`users/by-email?email=${email}`)
    },
    paymentMethod: (payload) => {
        return ApiService.post("users/payment-method", payload)
    },
    forgotPassword: (form) => {
        return ApiService.post("users/forgot", form)
    },
    resetPassword: (form) => {
        return ApiService.post("users/reset-password", form)
    },
    updateTemporaryPassword: (payload) => {
        return ApiService.post('users/new-password', payload);
    },
    getEmailPreference: ({ email, uid }) => {
        return ApiService.get(`email-preference/by-token?email=${encodeURIComponent(email)}&uid=${encodeURIComponent(uid)}`)
    },
    updateEmailPreference: ({ data, email }) => {
        return ApiService.put(`email-preference/unsubscribe?email=${encodeURIComponent(email)}`, data)
    },
    dataDeletionRequest: (formData) => {
        return ApiService.post("users/data-deletion-request", formData);
    }
}

export const EventsAPIService = {
    postUrlChange: (payload) => {
        return ApiService.post("events/url", payload);
    }
}

export const ConnectorAPIService = {
    getConnectors: async () => {
        try {
            const [connectorsData, categoriesData] = await Promise.all([
                ApiService.get("connector/public"),
                ApiService.get("connector/public/categories")
            ]);

            if (!Array.isArray(connectorsData)) {
                return [];
            }

            // Create a map of connector IDs to their categories
            const connectorCategories = new Map();
            if (Array.isArray(categoriesData)) {
                categoriesData.forEach(cat => {
                    const connectorId = cat.connector_id;
                    if (!connectorCategories.has(connectorId)) {
                        connectorCategories.set(connectorId, []);
                    }
                    connectorCategories.get(connectorId).push(cat.category_name);
                });
            }

            // Add categories to each connector
            const mappedConnectors = connectorsData.map(connector => ({
                ...connector,
                Category: connectorCategories.get(connector.ConnectorID)?.join(', ') || ''
            }));
            return mappedConnectors;
        } catch (error) {
            const status = error?.response?.status;
            const msg = error?.message || String(error);
            if (typeof console !== 'undefined' && console.warn) {
                console.warn(
                    '[ConnectorAPIService.getConnectors] Failed to load public connectors/categories.',
                    status ? `HTTP ${status}` : msg
                );
            }
            /* Avoid unhandled rejections on marketing pages (e.g. /pricing) when API is down, proxy misconfigured, or gateway times out (504). */
            return [];
        }
    },
    searchConnector: async (name) => {
        try {
            const response = await ApiService.get(`connector/public/search?query=${encodeURIComponent(name)}`);
            return response;
        } catch (error) {
            throw error;
        }
    },
    getGoogleImage: async (query, connectorName = null, url = null) => {
        try {
            // Always pass connectorName if provided - backend will use intelligent query generation
            let apiUrl = `connector/public/google-image?q=${encodeURIComponent(query)}`;
            if (connectorName) {
                apiUrl += `&connectorName=${encodeURIComponent(connectorName)}`;
                if (url) {
                    apiUrl += `&url=${encodeURIComponent(url)}`;
                }
            } else {
            }
            const response = await ApiService.get(apiUrl);
            return response;
        } catch (error) {
            throw error;
        }
    },
    getConnectorById: async (connectorId) => {
        try {
            const response = await ApiService.get(`connector/public/${connectorId}`);
            return response;
        } catch (error) {
            throw error;
        }
    },
    getConnectorCategories: async () => {
        try {
            const response = await ApiService.get(`connector/public/categories`);
            
            if (!response || !Array.isArray(response)) {
                return [];
            }

            // The response is already in the correct format, just ensure the flags are numbers
            const mappedResponse = response.map(category => ({
                category_name: category.category_name,
                source_flg: Number(category.source_flg),
                destination_flg: Number(category.destination_flg)
            }));
            return mappedResponse;
        } catch (error) {
            throw error;
        }
    },
    createConnectorRequest: (payload) => {
        return ApiService.post(`connector/request`, payload);
    },
    updateSearchResultLogo: (payload) => {
        return ApiService.put(`connector/public/search-result/logo`, payload);
    }
}

export const BillingAPIService = {
    getAverageRowsPerMin: () => {
        return ApiService.get("billing/average-rows-minute")
    }
}

export const MarketingEventLogAPIService = {
    addMarketingEventLog: (payload) => {
        return ApiService.post("marketing-event-log/add", payload)
    }
}

export const OrganizationAPIService = {
    getReviews: () => {
        return ApiService.get("organization/reviews");
    }
}

export const DocumentationApiService = {
    getConnectorDocumentation: (connectorId) => {
        return ApiService.get(`documentation/public/connector/${connectorId}`);
    },
    getDocumentation: () => {
        return ApiService.get(`documentation/public`);
    },
    getDocumentationCategories: () => {
        return ApiService.get(`documentation/public/categories`);
    },
    getDocumentationIcons: () => {
        return ApiService.get(`documentation/public/icons`);
    }
}

/** Public articles (blog) - from API; used by marketing site blog listing and article pages. */
export const ArticleApiService = {
    getArticles: () => ApiService.get('article'),
    getArticleBySlug: (slug) => ApiService.get(`article/${encodeURIComponent(slug)}`),
    /** Topics for filter chips – managed in Admin → Article Management → Topics. */
    getTopics: () => ApiService.get('article/topics'),
}

export const ChangelogAPIService = {
    getChangelog: () => {
        return ApiService.get('changelog');
    }
}

export const StatusAPIService = {
    // Check the main API status
    getApiStatus: async () => {
        try {
            const response = await axios.get(REACT_APP_API_URL, { timeout: 5000 });
            return {
                status: 'operational',
                responseTime: response.headers['x-response-time'] || 'N/A',
                version: response.data?.version || 'Unknown',
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            return {
                status: 'outage',
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    },

    // Check orchestrator API status (if available)
    getOrchestratorStatus: async () => {
        try {
            // Try to reach orchestrator API - adjust URL as needed
            const orchestratorUrl = process.env.REACT_APP_ORCHESTRATOR_URL || 'http://localhost:3001';
            const response = await axios.get(`${orchestratorUrl}/health`, { timeout: 5000 });
            return {
                status: 'operational',
                responseTime: response.headers['x-response-time'] || 'N/A',
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            return {
                status: 'outage',
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    },

    // Check database connectivity through API
    getDatabaseStatus: async () => {
        try {
            // Use a lightweight endpoint that requires DB access
            await ApiService.get('connector/public/categories');
            return {
                status: 'operational',
                responseTime: 'N/A',
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            return {
                status: 'outage',
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    },

    // Get overall system status
    getSystemStatus: async () => {
        try {
            const [apiStatus, dbStatus] = await Promise.allSettled([
                StatusAPIService.getApiStatus(),
                StatusAPIService.getDatabaseStatus()
            ]);

            const services = [
                {
                    id: 1,
                    name: 'Data Connector API',
                    status: apiStatus.status === 'fulfilled' ? apiStatus.value.status : 'outage',
                    description: 'Core API for data integration and ETL operations',
                    responseTime: apiStatus.status === 'fulfilled' ? apiStatus.value.responseTime : 'N/A',
                    version: apiStatus.status === 'fulfilled' ? apiStatus.value.version : 'Unknown'
                },
                {
                    id: 2,
                    name: 'Database Services',
                    status: dbStatus.status === 'fulfilled' ? dbStatus.value.status : 'outage',
                    description: 'Database connections and data storage services',
                    responseTime: dbStatus.status === 'fulfilled' ? dbStatus.value.responseTime : 'N/A'
                }
            ];

            // Calculate overall status
            const overallStatus = services.every(service => service.status === 'operational') ? 'operational' : 'degraded';

            return {
                overallStatus,
                services,
                lastChecked: new Date().toISOString()
            };
        } catch (error) {
            return {
                overallStatus: 'outage',
                services: [],
                error: error.message,
                lastChecked: new Date().toISOString()
            };
        }
    }
}
